import React, { useState, useEffect } from 'react';
import { createEmployee, fetchPortsList, fetchDesignationsList } from './services/api';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Card, Alert, Container, Spinner, Row, Col } from 'react-bootstrap';
import { Port, Designation } from './types';
import { useAuth } from './AuthContext';

const genderOptions = ['Male', 'Female', 'Other'];

const CreateEmployee: React.FC = () => {
  const [profileImage, setProfileImage] = useState('');
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [employeeCode, setEmployeeCode] = useState('');
  const [name, setName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [gender, setGender] = useState('');
  const [password, setPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [dateOfJoining, setDateOfJoining] = useState('');
  const [port, setPort] = useState<number | ''>('');
  const [designation, setDesignation] = useState<number | ''>('');
  const [ports, setPorts] = useState<Port[]>([]);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { token } = useAuth();

  useEffect(() => {
    const loadData = async () => {
      try {
        const [portsResponse, designationsResponse] = await Promise.all([
          fetchPortsList(),
          fetchDesignationsList()
        ]);

        const ports = Array.isArray(portsResponse.data) ? portsResponse.data : [];
        const designations = Array.isArray(designationsResponse.data) ? designationsResponse.data : [];

        setPorts(ports);
        setDesignations(designations);

        if (token && token.designation?.user_type === 'ADMIN' && token.port) {
          setPort(token.port);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load initial data');
      }
    };

    loadData();
  }, [token]);

  // Auto-generate employee code when port changes
  useEffect(() => {
    if (!port) {
      setEmployeeCode('');
      return;
    }
    const selectedPort = ports.find(p => p.id === port);
    if (selectedPort) {
      // Use first 3 letters of port name uppercased as code
      const portCode = selectedPort.name.slice(0, 3).toUpperCase() || 'EMP';
      // Generate a random 4-digit number (in real app, fetch next available from backend)
      const randomNum = Math.floor(1000 + Math.random() * 9000);
      setEmployeeCode(`${portCode}-${randomNum}`);
    }
  }, [port, ports]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await createEmployee({
        profile_image: profileImage || undefined,
        employee_code: employeeCode,
        name,
        mobile_number: mobileNumber,
        gender,
        password,
        date_of_birth: dateOfBirth,
        date_of_joining: dateOfJoining,
        port: Number(port),
        designation: Number(designation)
      });
      setSuccess(true);
      setTimeout(() => navigate('/employees'), 1200);
    } catch (err) {
      setError('Failed to create employee');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result as string);
        setProfileImage(reader.result as string); // Use data URL for now
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #e0f7fa 0%, #e0ffe0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Container className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <Card className="glass-card p-4 shadow-lg border-0" style={{ maxWidth: 540, width: '100%' }}>
          <Card.Body>
            <div className="text-center mb-4">
              <h2 className="fw-bold mb-1" style={{ color: '#156082' }}>Create Employee</h2>
              <div className="text-muted mb-3">Fill in the details to add a new employee</div>
              <div className="d-flex flex-column align-items-center mb-3">
                <label htmlFor="profileImageInput" style={{ cursor: 'pointer' }}>
                  {(profilePreview || profileImage) ? (
                    <img src={profilePreview || profileImage} alt="Preview" className="shadow" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: '50%', border: '3px solid #156082', background: '#fff' }} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center bg-light shadow" style={{ width: 96, height: 96, borderRadius: '50%', border: '3px solid #e8f4f8', fontSize: 36, color: '#156082' }}>
                      <i className="fas fa-user"></i>
                    </div>
                  )}
                  <Form.Control id="profileImageInput" type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                </label>
                <div className="small text-muted mt-2">Click to upload profile image</div>
              </div>
            </div>
            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="employeeCode">
                    <Form.Label>Employee Code</Form.Label>
                    <Form.Control type="text" value={employeeCode} readOnly disabled placeholder="Auto-generated" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="name">
                    <Form.Label>Name</Form.Label>
                    <Form.Control type="text" value={name} onChange={e => setName(e.target.value)} required maxLength={50} placeholder="Full name" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="mobileNumber">
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control type="text" value={mobileNumber} onChange={e => setMobileNumber(e.target.value)} required maxLength={15} placeholder="Mobile number" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="gender">
                    <Form.Label>Gender</Form.Label>
                    <Form.Select value={gender} onChange={e => setGender(e.target.value)} required>
                      <option value="">Select Gender</option>
                      {genderOptions.map(g => <option key={g} value={g}>{g}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="password">
                    <Form.Label>Password</Form.Label>
                    <Form.Control type="text" value={password} onChange={e => setPassword(e.target.value)} required maxLength={32} placeholder="Password" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="dateOfBirth">
                    <Form.Label>Date of Birth</Form.Label>
                    <Form.Control type="date" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} required />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="dateOfJoining">
                    <Form.Label>Date of Joining</Form.Label>
                    <Form.Control type="date" value={dateOfJoining} onChange={e => setDateOfJoining(e.target.value)} required />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="port">
                    <Form.Label>Location</Form.Label>
                    <Form.Select value={port} onChange={e => setPort(e.target.value === '' ? '' : Number(e.target.value))} required disabled={!!(token && token.designation?.user_type === 'ADMIN' && token.port)}>
                      <option value="">Select Location</option>
                      {ports.map((p: Port) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="designation">
                    <Form.Label>Designation</Form.Label>
                    <Form.Select value={designation} onChange={e => setDesignation(e.target.value === '' ? '' : Number(e.target.value))} required>
                      <option value="">Select Designation</option>
                      {designations.map((d: Designation) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
              {success && <Alert variant="success" className="mt-3">Employee created!</Alert>}
              <div className="d-flex justify-content-between mt-4">
                <Button type="button" variant="outline-secondary" onClick={() => navigate('/employees')} disabled={loading}>Back to List</Button>
                <Button type="submit" variant="primary" disabled={loading} className="px-4 py-2 fw-bold shadow">
                  {loading ? <Spinner size="sm" animation="border" /> : 'Create Employee'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default CreateEmployee; 